import * as THREE from 'three';
import {AbstractPointSnapStrategy} from './AbstractPointSnapStrategy';
import {PointSnapStrategyType} from './PointSnapStrategyType';
import type {PointSnapRequest, PointSnapResult} from './PointSnapTypes';
import type {AbstractSketchNearestPointProjector} from './projector/AbstractSketchNearestPointProjector';

/**
 * 靠近点捕捉策略。
 * 职责：鼠标悬停在草图边上时，按当前注册的解析投影器或显示折线后备
 * 计算鼠标世界坐标到目标边的最近点，并作为捕捉结果返回。
 */
export class NearestPointSnapStrategy extends AbstractPointSnapStrategy {
    /** 策略类型。 */
    public readonly strategyType: PointSnapStrategyType = PointSnapStrategyType.Nearest;

    /**
     * 最低兜底权重，保证所有高优先级捕捉（端点/中点/方向型）均可覆盖 Nearest。
     * 参考：Quadrant=85, Center=90, HV=700, Perpendicular=900, Tangent=1000。
     */
    private static readonly WEIGHT = 5;

    /** 复用向量，避免热路径 new 分配。 */
    private readonly _ab = new THREE.Vector3();
    private readonly _ap = new THREE.Vector3();
    private readonly _proj = new THREE.Vector3();
    private readonly _worldA = new THREE.Vector3();
    private readonly _worldB = new THREE.Vector3();

    /**
     * 创建靠近点捕捉策略。
     *
     * @param analyticProjectors 按优先级排列的解析草图边投影器。
     */
    public constructor(
        private readonly analyticProjectors: readonly AbstractSketchNearestPointProjector[] = [],
    ) {
        super();
    }

    /**
     * 执行靠近点捕捉。
     * 说明：
     * 1. 仅对 hoveredObject 为草图边（isAbstractSketchEdgeObject=true）时触发；
     * 2. 优先通过曲线族投影器解析真实设计几何上的最近点；
     * 3. 未注册解析投影器的实体才回退到 BufferGeometry 采样折线；
     * 4. 草图边 geometry 顶点为世界坐标（由 SketchPointObject.getPosition() 直接写入），
     *    无需再应用 matrixWorld，与 computeEdgeScreenDistance 保持一致。
     * @param request 点捕捉请求参数。
     * @returns 捕捉到最近点时返回结果，否则返回 null。
     */
    public resolve(request: PointSnapRequest): PointSnapResult | null {
        const hoveredObject = request.hoveredObject;
        if (!hoveredObject) {
            return null;
        }
        if (this.isSnapTargetExcluded(request, hoveredObject)) {
            return null;
        }

        // 通过运行时标记确认是草图边，不直接 import 具体类避免循环依赖。
        if (Reflect.get(hoveredObject, 'isAbstractSketchEdgeObject') !== true) {
            return null;
        }

        // 已注册的基础解析曲线优先按数学定义投影。
        const analyticPoint = this.resolveAnalyticNearestPoint(hoveredObject, request.worldPosition);
        if (analyticPoint) {
            return this.createResult(analyticPoint, hoveredObject);
        }

        // 未注册解析投影器的草图边使用显示折线，与当前强力裁剪的几何口径保持一致。
        const line = hoveredObject as THREE.Line;
        const geometry = line.geometry;
        if (!geometry) {
            return null;
        }

        const posAttr = geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
        if (!posAttr || posAttr.count < 2) {
            return null;
        }

        const worldPos = request.worldPosition;
        let minDistSq = Infinity;
        let closestPoint: THREE.Vector3 | null = null;

        // 遍历相邻顶点对（草图 geometry 顶点已是世界坐标），找离鼠标最近的线段上的点。
        for (let i = 0; i < posAttr.count - 1; i++) {
            this._worldA.set(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i));
            this._worldB.set(posAttr.getX(i + 1), posAttr.getY(i + 1), posAttr.getZ(i + 1));

            this._ab.subVectors(this._worldB, this._worldA);
            this._ap.subVectors(worldPos, this._worldA);

            const abLenSq = this._ab.lengthSq();
            // 退化为点时跳过（避免除以零）。
            if (abLenSq < 1e-18) {
                continue;
            }

            // 将鼠标点投影到线段，参数 t 夹紧在 [0,1] 以限制在线段范围内。
            const t = Math.max(0, Math.min(1, this._ap.dot(this._ab) / abLenSq));
            this._proj.copy(this._worldA).addScaledVector(this._ab, t);

            const distSq = worldPos.distanceToSquared(this._proj);
            if (distSq < minDistSq) {
                minDistSq = distSq;
                closestPoint = this._proj.clone();
            }
        }

        if (closestPoint === null) {
            return null;
        }

        return this.createResult(closestPoint, hoveredObject);
    }

    /**
     * 按注册顺序解析目标草图边的解析最近点。
     *
     * @param targetObject 目标草图边。
     * @param worldPosition 待投影世界坐标。
     * @returns 解析几何上的最近点；没有投影器支持时返回 null。
     */
    private resolveAnalyticNearestPoint(
        targetObject: THREE.Object3D,
        worldPosition: THREE.Vector3,
    ): THREE.Vector3 | null {
        for (const projector of this.analyticProjectors) {
            if (!projector.supports(targetObject)) {
                continue;
            }
            return projector.project(targetObject, worldPosition);
        }
        return null;
    }

    /**
     * 创建统一最近点捕捉结果。
     *
     * @param position 命中世界坐标。
     * @param targetObject 命中草图边。
     * @returns 最近点捕捉结果。
     */
    private createResult(
        position: THREE.Vector3,
        targetObject: THREE.Object3D,
    ): PointSnapResult {
        return {
            strategyType: PointSnapStrategyType.Nearest,
            position,
            targetObject,
            weight: NearestPointSnapStrategy.WEIGHT,
        };
    }
}
